import React, { useCallback, useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { docTypes, DOCUMENT_TYPES } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { CustomButton, CustomSelect, Iframe } from 'components';
import { Box, MenuItem } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import {
    useSendReplaceAddressVerificationMutation,
    useSendReplaceIdVerificationMutation,
    useStartReplaceAddressVerificationMutation,
    useStartReplaceIdVerificationMutation,
} from 'api/main';

const useStyles = makeStyles(theme => ({
    selectDocType: {
        marginRight: 10,
        marginBottom: 0,
    },
}));

const renderOption = ({ key, name }) => (
    <MenuItem key={key} value={key}>
        {name}
    </MenuItem>
);

const JumioReplaceIframe = ({ doc, onFinish }) => {
    const classes = useStyles();
    const [selectedDocumentType, setSelectedDocumentType] = useState(DOCUMENT_TYPES[0]);
    const [showIframe, setShowIframe] = useState(false);
    const [showDocTypeSelect, setShowDocTypeSelect] = useState(false);
    const [
        startReplaceIdVerification,
        { isSuccess: startReplaceIdSuccess, data: replaceIdData, reset: resetIdVerification },
    ] = useStartReplaceIdVerificationMutation();
    const [
        startReplaceAddressVerification,
        {
            isSuccess: startReplaceAddressSuccess,
            data: replaceAddressData,
            reset: resetAddressVerification,
        },
    ] = useStartReplaceAddressVerificationMutation();
    const [sendReplaceIdVerification, { isSuccess: idReplaceSuccess }] =
        useSendReplaceIdVerificationMutation();
    const [sendReplaceAddressVerification, { isSuccess: addressReplaceSuccess }] =
        useSendReplaceAddressVerificationMutation();

    const jumioSession = useMemo(
        () =>
            doc.type === docTypes.ID || doc.type === docTypes.SECONDARY_ID
                ? replaceIdData
                : replaceAddressData,
        [doc.type, replaceIdData, replaceAddressData]
    );

    const handleMessage = useCallback(
        e => {
            if (!e.data || typeof e.data !== 'string') return;
            try {
                const data = window.JSON.parse(e.data);
                const { value } = data.payload;

                if (value === 'success') {
                    // send verification and handle callback to hide the iframe
                    if (doc.type === docTypes.ID || doc.type === docTypes.SECONDARY_ID) {
                        sendReplaceIdVerification(doc.id);
                    } else {
                        sendReplaceAddressVerification(doc.id);
                    }
                }
                if (value === 'error' || value === 'redirectError') {
                    setShowIframe(false);
                    onFinish();
                }
            } catch (error) {
                console.log(error);
            }
        },
        [sendReplaceIdVerification, sendReplaceAddressVerification, onFinish, doc]
    );

    useEffect(() => {
        window.addEventListener('message', handleMessage, false);

        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [handleMessage]);

    useEffect(() => {
        if (idReplaceSuccess) {
            resetIdVerification();
            setShowIframe(false);
            onFinish();
        }
    }, [idReplaceSuccess, onFinish, resetIdVerification]);

    useEffect(() => {
        if (addressReplaceSuccess) {
            resetAddressVerification();
            setShowIframe(false);
            onFinish();
        }
    }, [addressReplaceSuccess, onFinish, resetAddressVerification]);

    const initJumioSession = useCallback(() => {
        if (doc.type === docTypes.ID || doc.type === docTypes.SECONDARY_ID) {
            startReplaceIdVerification(doc.id);
            setShowIframe(true);
        } else {
            setShowDocTypeSelect(true);
        }
    }, [doc, startReplaceIdVerification]);

    useEffect(() => {
        initJumioSession();
    }, [initJumioSession]);

    useEffect(() => {
        if (startReplaceIdSuccess || startReplaceAddressSuccess) {
            setShowIframe(true);
        }
    }, [startReplaceIdSuccess, startReplaceAddressSuccess]);

    const initJumioSessionForAddress = useCallback(() => {
        if (doc.type === docTypes.FACTURE && selectedDocumentType) {
            setShowDocTypeSelect(false);
            startReplaceAddressVerification({
                id: doc.id,
                addressDocumentType: selectedDocumentType.type,
            });
        }
    }, [startReplaceAddressVerification, doc, selectedDocumentType]);

    const handleDocTypeChange = useCallback(e => {
        const { value } = e.target;
        const documentType = DOCUMENT_TYPES.find(d => d.key === value);
        if (documentType) {
            setSelectedDocumentType(documentType);
        }
    }, []);

    const handleSubmitDocType = useCallback(() => {
        initJumioSessionForAddress();
    }, [initJumioSessionForAddress]);

    return (
        <Box display="flex" flexDirection="column" mb={2}>
            {showDocTypeSelect && doc.type === docTypes.FACTURE && (
                <Box display="flex" alignItems="center">
                    <CustomSelect
                        value={selectedDocumentType.key}
                        onChange={handleDocTypeChange}
                        inputStyle={classes.selectDocType}
                    >
                        {DOCUMENT_TYPES.map(renderOption)}
                    </CustomSelect>

                    <CustomButton noMargin label={texts.SUIVANT} onClick={handleSubmitDocType} />
                </Box>
            )}
            {showIframe && (
                <Iframe
                    title={texts.ID_CHECK}
                    allow="camera;fullscreen"
                    src={jumioSession?.redirectUrl}
                />
            )}
        </Box>
    );
};

JumioReplaceIframe.propTypes = {
    doc: PropTypes.object,
    onFinish: PropTypes.func,
};

export default JumioReplaceIframe;
